/**
 * Choosing passages for the Random Paragraph Generator: the filters, the length bands
 * and the draw itself.
 *
 * The draw takes distinct passages with the shared unbiased sampler, so no passage
 * repeats within a batch and every passage that matches the filters is equally likely.
 */

import {
  PASSAGES,
  type Passage,
  type Topic,
} from "@/lib/tool-kit/random-fun/paragraph-passages";
import { sample, type Uint32Source } from "@/lib/tool-kit/random-fun/rng";

export const LENGTH_BANDS = ["short", "medium", "long"] as const;
export type LengthBand = (typeof LENGTH_BANDS)[number];

/** A short passage has at most this many words… */
export const SHORT_MAX_WORDS = 60;
/** …a medium one at most this many; anything longer is long. */
export const MEDIUM_MAX_WORDS = 120;

export const MIN_COUNT = 1;
export const MAX_COUNT = 5;
/** A typist's pace used for the time estimate, in words per minute. */
export const TYPING_WPM = 40;

const RE_SPACES = /\s+/;

export function wordCount(text: string): number {
  const trimmed = text.trim();
  return trimmed === "" ? 0 : trimmed.split(RE_SPACES).length;
}

export function lengthBandOf(text: string): LengthBand {
  const words = wordCount(text);
  if (words <= SHORT_MAX_WORDS) {
    return "short";
  }
  return words <= MEDIUM_MAX_WORDS ? "medium" : "long";
}

/** The passages that match a topic and a length band ("any" matches everything). */
export function filterPassages(
  topic: Topic | "any",
  band: LengthBand | "any",
  passages: readonly Passage[] = PASSAGES
): Passage[] {
  return passages.filter(
    (passage) =>
      (topic === "any" || passage.topic === topic) &&
      (band === "any" || lengthBandOf(passage.text) === band)
  );
}

/** A count held to the 1–5 the page offers; anything unreadable becomes the minimum. */
export function clampCount(value: number): number {
  if (!Number.isFinite(value)) {
    return MIN_COUNT;
  }
  return Math.min(MAX_COUNT, Math.max(MIN_COUNT, Math.trunc(value)));
}

/**
 * Up to `count` different passages that match the filters, in random order. When fewer
 * match, all of them are returned and the caller says so.
 */
export function drawPassages(
  count: number,
  topic: Topic | "any",
  band: LengthBand | "any",
  source?: Uint32Source,
  passages: readonly Passage[] = PASSAGES
): Passage[] {
  return sample(
    filterPassages(topic, band, passages),
    clampCount(count),
    source
  );
}

/** Seconds to type the text at `TYPING_WPM`, rounded to the nearest 5. */
export function typingSeconds(text: string): number {
  const seconds = (wordCount(text) / TYPING_WPM) * 60;
  return Math.max(5, Math.round(seconds / 5) * 5);
}
